/**
 * Gamification Anti-Abuse System
 * 
 * Provides guardrails to prevent and detect abuse of the gamification system.
 * 
 * ABUSE VECTORS ADDRESSED:
 * 1. Stage cycling - Changing opportunity stages back and forth
 * 2. Create/delete abuse - Creating then deleting entities for points
 * 3. Minimal updates - Trivial edits (adding/removing spaces) for points
 * 4. Rapid-fire actions - Spamming the same action repeatedly
 * 5. Import abuse - Bulk imports generating excessive points
 * 6. Self-feedback - Submitting feedback on own content
 * 
 * STRATEGIES:
 * - Cooldown periods between repeat actions
 * - Minimum content requirements for creation points
 * - Stage advancement validation (no backwards movement points)
 * - Change significance detection
 * - Daily/hourly action limits
 * - Source tracking (manual vs import vs automation)
 */

import { db } from './firebase';
import { collectionPath } from './firestoreCollections';
import { POINT_VALUES } from './gamificationUtils';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

// ============================================
// ANTI-ABUSE CONFIGURATION
// ============================================

/**
 * Cooldown periods (in seconds) between earning points for the same action
 * This prevents rapid-fire point farming
 */
export const ACTION_COOLDOWNS = {
  // Opportunities
  OPPORTUNITY_CREATED: 60,           // 1 minute between creating opportunities
  OPPORTUNITY_UPDATED: 300,          // 5 minutes between earning update points
  OPPORTUNITY_STAGE_ADVANCED: 3600,  // 1 hour between stage advancement points (per opportunity)
  OPPORTUNITY_WON: 0,                // No cooldown - winning deals is the goal!
  
  // Customers/Contacts
  CUSTOMER_CREATED: 60,              // 1 minute between customer creations
  CUSTOMER_UPDATED: 300,             // 5 minutes between update points
  CONTACT_CREATED: 60,               // 1 minute between contact creations
  CONTACT_UPDATED: 300,              // 5 minutes between contact update points
  
  // Tasks
  TASK_CREATED: 30,                  // 30 seconds between task creations
  TASK_COMPLETED: 0,                 // No cooldown - completing tasks is good!
  TASK_COMPLETED_ON_TIME: 0,
  TASK_OVERDUE_COMPLETED: 0,
  
  // Notes
  NOTE_CREATED: 60,                  // 1 minute between notes
  
  // Feedback
  FEEDBACK_SUBMITTED: 300,           // 5 minutes between feedback submissions
  FEEDBACK_HELPFUL_SUBMITTED: 300,
  FAQ_FEEDBACK_SUBMITTED: 60,        // 1 minute between FAQ feedback
  
  // Campaigns
  CAMPAIGN_SENT: 3600,               // 1 hour between campaign sends
  
  // Login (handled separately)
  DAILY_LOGIN: 0
};

/**
 * Daily limits per action type
 * Prevents excessive point farming even with cooldowns
 */
export const DAILY_ACTION_LIMITS = {
  OPPORTUNITY_CREATED: 20,           // Max 20 manual opportunity creates per day
  OPPORTUNITY_UPDATED: 50,           // Max 50 update point awards per day
  OPPORTUNITY_STAGE_ADVANCED: 30,    // Max 30 stage advancements per day
  OPPORTUNITY_WON: 50,               // High limit - winning is the goal
  
  CUSTOMER_CREATED: 30,
  CUSTOMER_UPDATED: 50,
  CONTACT_CREATED: 50,
  CONTACT_UPDATED: 50,
  
  TASK_CREATED: 50,
  TASK_COMPLETED: 100,               // High limit - completing tasks is good
  
  NOTE_CREATED: 100,                 // Notes are valuable, high limit
  
  FEEDBACK_SUBMITTED: 20,
  FAQ_FEEDBACK_SUBMITTED: 30,
  
  CAMPAIGN_SENT: 5                   // Max 5 campaigns per day
};

/**
 * Minimum content requirements for creation points
 * Ensures meaningful content is being created
 */
export const MINIMUM_CONTENT_REQUIREMENTS = {
  OPPORTUNITY_CREATED: {
    requiredFields: ['customerName', 'value'],
    minValueLength: { name: 3 },
    minNumericValue: { value: 0 }
  },
  CUSTOMER_CREATED: {
    requiredFields: ['customerName'],
    minValueLength: { customerName: 3 }
  },
  NOTE_CREATED: {
    requiredFields: ['content'],
    minValueLength: { content: 10 }  // At least 10 characters
  },
  TASK_CREATED: {
    requiredFields: ['title'],
    minValueLength: { title: 5 }
  }
};

/**
 * Stage progression order for validation
 * Points only awarded for forward movement
 */
export const OPPORTUNITY_STAGE_ORDER = [
  'New',
  'Qualified',
  'Proposal Sent',
  'Closed Won',
  'Not Qualified',
  'Closed Lost'
];

/**
 * Actions that don't earn points (terminal or negative stages)
 */
export const NON_SCORING_STAGES = ['Not Qualified', 'Closed Lost'];

/**
 * Actions that should only award points ONCE per entity ID
 * Key is action type, value is the specific identifier to track
 * e.g., OPPORTUNITY_WON should only award points once per opportunity ID
 * even if moved back from "Closed Won" and then to "Closed Won" again
 */
export const ENTITY_ONCE_ACTIONS = {
  // Opportunity actions - tracked per opportunity ID
  OPPORTUNITY_WON: true,             // Can only win an opportunity once
  OPPORTUNITY_STAGE_ADVANCED: 'stage', // Track per stage reached (entityId_stage)
  
  // Task actions - tracked per task ID
  TASK_COMPLETED: true,              // Can only complete a task once
  TASK_COMPLETED_ON_TIME: true,      // Can only complete on-time once
  TASK_OVERDUE_COMPLETED: true,      // Can only complete overdue once
  
  // Customer/Contact actions - tracked per entity ID
  CUSTOMER_CREATED: true,            // Can only create a customer once
  CONTACT_CREATED: true,             // Can only create a contact once
  
  // Note actions - tracked per note ID
  NOTE_CREATED: true,                // Can only create a note once
  
  // Opportunity created - tracked per opportunity ID
  OPPORTUNITY_CREATED: true          // Can only create an opportunity once
};

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Check if user is within cooldown period for an action
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @param {string} entityId - Optional entity ID for per-entity cooldowns
 * @returns {Promise<{allowed: boolean, remainingSeconds: number, reason: string}>}
 */
export async function checkCooldown(userId, action, entityId = null) {
  const cooldownSeconds = ACTION_COOLDOWNS[action];
  
  // No cooldown configured
  if (!cooldownSeconds || cooldownSeconds === 0) {
    return { allowed: true, remainingSeconds: 0, reason: null };
  }
  
  try {
    const cooldownRef = doc(db, collectionPath('gamificationCooldowns'), `${userId}_${action}${entityId ? `_${entityId}` : ''}`);
    const cooldownSnap = await getDoc(cooldownRef);
    
    if (!cooldownSnap.exists()) {
      return { allowed: true, remainingSeconds: 0, reason: null };
    }
    
    const lastAction = cooldownSnap.data().lastAction;
    const lastActionTime = lastAction instanceof Timestamp ? lastAction.toDate() : new Date(lastAction);
    const now = new Date();
    const elapsedSeconds = Math.floor((now - lastActionTime) / 1000);
    
    if (elapsedSeconds < cooldownSeconds) {
      const remainingSeconds = cooldownSeconds - elapsedSeconds;
      return {
        allowed: false,
        remainingSeconds,
        reason: `cooldown_active`,
        message: `Please wait ${formatDuration(remainingSeconds)} before this action earns points again.`
      };
    }
    
    return { allowed: true, remainingSeconds: 0, reason: null };
  } catch (error) {
    console.error('[AntiAbuse] Cooldown check failed:', error);
    // On error, allow the action (fail-open for user experience)
    return { allowed: true, remainingSeconds: 0, reason: null };
  }
}

/**
 * Record action timestamp for cooldown tracking
 */
export async function recordActionTime(userId, action, entityId = null) {
  try {
    const cooldownRef = doc(db, collectionPath('gamificationCooldowns'), `${userId}_${action}${entityId ? `_${entityId}` : ''}`);
    await setDoc(cooldownRef, {
      userId,
      action,
      entityId,
      lastAction: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('[AntiAbuse] Failed to record action time:', error);
  }
}

/**
 * Check if points have already been awarded for this entity+action combination
 * Prevents awarding points twice for the same action on the same entity
 * e.g., can't get points for completing the same task twice
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @param {string} entityId - Entity ID (required for this check)
 * @param {string} stageOrVariant - Optional stage/variant for stage-based tracking
 * @returns {Promise<{alreadyAwarded: boolean, awardedAt: Date|null}>}
 */
export async function checkEntityActionAlreadyAwarded(userId, action, entityId, stageOrVariant = null) {
  if (!entityId) {
    // No entity ID provided - can't check
    return { alreadyAwarded: false, awardedAt: null };
  }
  
  // Check if this action should be deduplicated per entity
  const onceConfig = ENTITY_ONCE_ACTIONS[action];
  if (!onceConfig) {
    // Not configured for deduplication
    return { alreadyAwarded: false, awardedAt: null };
  }
  
  try {
    // Build the tracking key
    // If onceConfig === 'stage', append the stage to differentiate by stage reached
    let trackingKey = `${userId}_${action}_${entityId}`;
    if (onceConfig === 'stage' && stageOrVariant) {
      trackingKey = `${userId}_${action}_${entityId}_${stageOrVariant}`;
    }
    
    const awardedRef = doc(db, collectionPath('gamificationEntityAwards'), trackingKey);
    const awardedSnap = await getDoc(awardedRef);
    
    if (awardedSnap.exists()) {
      const data = awardedSnap.data();
      const awardedAt = data.awardedAt instanceof Timestamp 
        ? data.awardedAt.toDate() 
        : (data.awardedAt ? new Date(data.awardedAt) : null);
      
      return { alreadyAwarded: true, awardedAt };
    }
    
    return { alreadyAwarded: false, awardedAt: null };
  } catch (error) {
    console.error('[AntiAbuse] Entity award check failed:', error);
    // Fail-open for user experience
    return { alreadyAwarded: false, awardedAt: null };
  }
}

/**
 * Record that points were awarded for this entity+action combination
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @param {string} entityId - Entity ID
 * @param {string} stageOrVariant - Optional stage/variant for stage-based tracking
 */
export async function recordEntityActionAwarded(userId, action, entityId, stageOrVariant = null) {
  if (!entityId) return;
  
  const onceConfig = ENTITY_ONCE_ACTIONS[action];
  if (!onceConfig) return;
  
  try {
    let trackingKey = `${userId}_${action}_${entityId}`;
    if (onceConfig === 'stage' && stageOrVariant) {
      trackingKey = `${userId}_${action}_${entityId}_${stageOrVariant}`;
    }
    
    const awardedRef = doc(db, collectionPath('gamificationEntityAwards'), trackingKey);
    await setDoc(awardedRef, {
      userId,
      action,
      entityId,
      stageOrVariant: stageOrVariant || null,
      awardedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('[AntiAbuse] Failed to record entity award:', error);
  }
}

/**
 * Check if user has exceeded daily action limit
 * @returns {Promise<{allowed: boolean, count: number, limit: number, reason: string}>}
 */
export async function checkDailyLimit(userId, action) {
  const dailyLimit = DAILY_ACTION_LIMITS[action];
  
  // No limit configured
  if (!dailyLimit) {
    return { allowed: true, count: 0, limit: Infinity, reason: null };
  }
  
  try {
    const today = new Date().toISOString().split('T')[0];
    const countRef = doc(db, collectionPath('gamificationDailyCounts'), `${userId}_${action}_${today}`);
    const countSnap = await getDoc(countRef);
    
    const currentCount = countSnap.exists() ? countSnap.data().count : 0;
    
    if (currentCount >= dailyLimit) {
      return {
        allowed: false,
        count: currentCount,
        limit: dailyLimit,
        reason: 'daily_limit_reached',
        message: `Daily award points limit reached for this action (${dailyLimit}). Try again tomorrow!`
      };
    }
    
    return { allowed: true, count: currentCount, limit: dailyLimit, reason: null };
  } catch (error) {
    console.error('[AntiAbuse] Daily limit check failed:', error);
    return { allowed: true, count: 0, limit: dailyLimit, reason: null };
  }
}

/**
 * Increment daily action count
 */
export async function incrementDailyCount(userId, action) {
  try {
    const today = new Date().toISOString().split('T')[0];
    const countRef = doc(db, collectionPath('gamificationDailyCounts'), `${userId}_${action}_${today}`);
    const countSnap = await getDoc(countRef);
    
    const currentCount = countSnap.exists() ? countSnap.data().count : 0;
    
    await setDoc(countRef, {
      userId,
      action,
      date: today,
      count: currentCount + 1,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('[AntiAbuse] Failed to increment daily count:', error);
  }
}

/**
 * Validate minimum content requirements for entity creation
 * @returns {{valid: boolean, reason: string, missingFields: string[]}}
 */
export function validateMinimumContent(action, entityData) {
  const requirements = MINIMUM_CONTENT_REQUIREMENTS[action];
  
  // No requirements for this action
  if (!requirements) {
    return { valid: true, reason: null, missingFields: [] };
  }
  
  const missingFields = [];
  const invalidFields = [];
  
  // Check required fields exist
  if (requirements.requiredFields) {
    for (const field of requirements.requiredFields) {
      const value = entityData[field];
      if (value === undefined || value === null || value === '') {
        missingFields.push(field);
      }
    }
  }
  
  // Check minimum string lengths
  if (requirements.minValueLength) {
    for (const [field, minLength] of Object.entries(requirements.minValueLength)) {
      const value = entityData[field];
      if (value && typeof value === 'string' && value.trim().length < minLength) {
        invalidFields.push(`${field} must be at least ${minLength} characters`);
      }
    }
  }
  
  // Check minimum numeric values
  if (requirements.minNumericValue) {
    for (const [field, minValue] of Object.entries(requirements.minNumericValue)) {
      const value = entityData[field];
      if (value !== undefined && (isNaN(value) || Number(value) < minValue)) {
        invalidFields.push(`${field} must be at least ${minValue}`);
      }
    }
  }
  
  if (missingFields.length > 0 || invalidFields.length > 0) {
    return {
      valid: false,
      reason: 'minimum_content_not_met',
      message: 'Entity does not meet minimum content requirements for award points.',
      missingFields,
      invalidFields
    };
  }
  
  return { valid: true, reason: null, missingFields: [], invalidFields: [] };
}

/**
 * Validate stage advancement is forward progression
 * @returns {{valid: boolean, isAdvancement: boolean, reason: string}}
 */
export function validateStageAdvancement(oldStage, newStage) {
  // Same stage - no points
  if (oldStage === newStage) {
    return { valid: false, isAdvancement: false, reason: 'same_stage' };
  }
  
  // Moving to non-scoring stage
  if (NON_SCORING_STAGES.includes(newStage)) {
    return { valid: false, isAdvancement: false, reason: 'non_scoring_stage' };
  }
  
  const oldIndex = OPPORTUNITY_STAGE_ORDER.indexOf(oldStage);
  const newIndex = OPPORTUNITY_STAGE_ORDER.indexOf(newStage);
  
  // Unknown stages - allow but log
  if (oldIndex === -1 || newIndex === -1) {
    console.warn('[AntiAbuse] Unknown stage in progression:', { oldStage, newStage });
    return { valid: true, isAdvancement: true, reason: null };
  }
  
  // Forward progression
  if (newIndex > oldIndex) {
    return { valid: true, isAdvancement: true, reason: null };
  }
  
  // Backward movement - no points
  return {
    valid: false,
    isAdvancement: false,
    reason: 'backward_movement',
    message: 'Award points are only given for forward stage progression.'
  };
}

/**
 * Detect if an update is meaningful (not just whitespace changes)
 * @param {object} oldData - Previous entity data
 * @param {object} newData - New entity data
 * @param {string[]} fieldsToCheck - Fields to compare
 * @returns {{meaningful: boolean, changedFields: string[]}}
 */
export function detectMeaningfulChanges(oldData, newData, fieldsToCheck) {
  const changedFields = [];
  
  for (const field of fieldsToCheck) {
    const oldValue = normalizeValue(oldData?.[field]);
    const newValue = normalizeValue(newData?.[field]);
    
    // Actual content change (not just whitespace)
    if (oldValue !== newValue) {
      changedFields.push(field);
    }
  }
  
  return {
    meaningful: changedFields.length > 0,
    changedFields,
    reason: changedFields.length === 0 ? 'no_meaningful_changes' : null
  };
}

/**
 * Normalize value for comparison (trim whitespace, lowercase for comparison)
 */
function normalizeValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim().replace(/\s+/g, ' ');
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) return JSON.stringify(value.sort());
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Format duration in human-readable form
 */
function formatDuration(seconds) {
  if (seconds < 60) return `${seconds} seconds`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)} minutes`;
  return `${Math.ceil(seconds / 3600)} hours`;
}

// ============================================
// COMPREHENSIVE VALIDATION
// ============================================

/**
 * Log blocked point activity for admin review
 * Silently logs when points would have been blocked - does not show to user
 * 
 * @param {string} userId - User who attempted the action
 * @param {string} action - Action type (e.g., 'OPPORTUNITY_CREATED')
 * @param {string} reason - Human-readable reason for blocking
 * @param {object} details - Additional context details
 */
async function logBlockedActivity(userId, action, reason, details) {
  try {
    // Get the point value that would have been awarded
    const pointsWouldHaveAwarded = POINT_VALUES[action] || 0;
    
    // Format the reason for better display
    const formattedReason = formatBlockReason(reason);
    
    const logRef = doc(collection(db, collectionPath('gamificationBlockedLog')));
    await setDoc(logRef, {
      userId,
      action,
      blockReason: formattedReason,  // UI expects blockReason
      reason, // Keep original for backward compatibility
      details,
      pointsWouldHaveAwarded,
      reviewed: false,
      awarded: false,
      timestamp: serverTimestamp(), // UI expects timestamp
      createdAt: serverTimestamp() // Keep for backward compatibility
    });
  } catch (error) {
    console.error('[AntiAbuse] Failed to log blocked activity:', error);
  }
}

/**
 * Format block reason into human-readable text
 */
function formatBlockReason(reason) {
  const reasonMap = {
    'import_source': 'Import Activity (Tracking Only)',
    'automated_action': 'Automated Action (Tracking Only)',
    'entity_already_awarded': 'Already Awarded for Entity',
    'cooldown_active': 'Cooldown Active',
    'daily_limit_reached': 'Daily Limit Reached',
    'insufficient_content': 'Insufficient Content',
    'no_meaningful_changes': 'No Meaningful Changes',
    'stage_not_advancing': 'Stage Not Advancing',
    'backward_stage_movement': 'Backward Stage Movement'
  };
  return reasonMap[reason] || reason;
}

/**
 * Comprehensive validation for point-earning actions
 * Now returns warnings instead of blocking - activity is logged for admin review
 * 
 * PHILOSOPHY: Never discourage legitimate app usage. Log potential abuse silently
 * and let admins review/adjust rather than blocking users from earning points.
 * 
 * @param {string} userId - User performing action
 * @param {string} action - Action type (e.g., 'OPPORTUNITY_CREATED')
 * @param {object} options - Additional context
 * @returns {Promise<{allowed: boolean, warnings: string[], reason: string, details: object}>}
 */
export async function validatePointAction(userId, action, options = {}) {
  const {
    entityId = null,
    entityData = {},
    oldData = null,
    source = 'manual',  // 'manual' | 'import' | 'automation' | 'workflow'
    fieldsToCheck = []
  } = options;
  
  const warnings = [];
  
  // 1. Track source but allow all sources to earn points
  // Import and automation sources are logged for admin visibility but NOT blocked
  if (source === 'import') {
    warnings.push('Activity from Smart Import');
    // Log for admin visibility but ALLOW points
    await logBlockedActivity(userId, action, 'import_source', { 
      source, 
      entityId,
      note: 'Smart Import activity - points awarded normally'
    });
  }
  
  if (source === 'automation' || source === 'workflow') {
    warnings.push('Automated/workflow activity');
    // Log for tracking but ALLOW points for automated actions too
    await logBlockedActivity(userId, action, 'automated_action', { 
      source, 
      entityId,
      note: 'Automated activity - logged for tracking'
    });
  }
  
  // 2. Check if points already awarded for this entity+action (deduplication)
  // For stage advancement, pass the new stage as the variant
  const stageVariant = action === 'OPPORTUNITY_STAGE_ADVANCED' ? options.newStage : null;
  const entityAwardCheck = await checkEntityActionAlreadyAwarded(userId, action, entityId, stageVariant);
  if (entityAwardCheck.alreadyAwarded) {
    warnings.push('Points already awarded for this entity');
    await logBlockedActivity(userId, action, 'entity_already_awarded', {
      entityId,
      stageVariant,
      awardedAt: entityAwardCheck.awardedAt
    });
    // Block - can't earn points twice for the same action on the same entity
    return {
      allowed: false,
      reason: 'entity_already_awarded',
      message: null, // Silent
      warnings,
      details: { entityId, awardedAt: entityAwardCheck.awardedAt }
    };
  }
  
  // 3. Check cooldown - log but don't block
  const cooldownResult = await checkCooldown(userId, action, entityId);
  if (!cooldownResult.allowed) {
    warnings.push(`Cooldown active (${formatDuration(cooldownResult.remainingSeconds)} remaining)`);
    await logBlockedActivity(userId, action, cooldownResult.reason, {
      remainingSeconds: cooldownResult.remainingSeconds,
      entityId
    });
    // Return blocked for cooldown - this is the main abuse prevention
    return {
      allowed: false,
      reason: cooldownResult.reason,
      message: null, // No message shown to user
      warnings,
      details: { remainingSeconds: cooldownResult.remainingSeconds }
    };
  }
  
  // 4. Check daily limit - log but don't block (just track)
  const limitResult = await checkDailyLimit(userId, action);
  if (!limitResult.allowed) {
    warnings.push(`Daily limit reached (${limitResult.count}/${limitResult.limit})`);
    await logBlockedActivity(userId, action, limitResult.reason, {
      count: limitResult.count,
      limit: limitResult.limit
    });
    // Return blocked for daily limit - prevents excessive farming
    return {
      allowed: false,
      reason: limitResult.reason,
      message: null, // No message shown to user
      warnings,
      details: { count: limitResult.count, limit: limitResult.limit }
    };
  }
  
  // 5. Check minimum content for creation actions - just log, don't block
  if (action.includes('CREATED')) {
    const contentResult = validateMinimumContent(action, entityData);
    if (!contentResult.valid) {
      warnings.push('Minimum content not met');
      await logBlockedActivity(userId, action, contentResult.reason, {
        missingFields: contentResult.missingFields,
        invalidFields: contentResult.invalidFields,
        entityId
      });
      // Still allow points - just log for review
    }
  }
  
  // 6. Check meaningful changes for update actions - log but allow
  if (action.includes('UPDATED') && oldData && fieldsToCheck.length > 0) {
    const changesResult = detectMeaningfulChanges(oldData, entityData, fieldsToCheck);
    if (!changesResult.meaningful) {
      warnings.push('No meaningful changes detected');
      await logBlockedActivity(userId, action, changesResult.reason, {
        changedFields: changesResult.changedFields,
        entityId
      });
      // Block for no meaningful changes - prevents whitespace abuse
      return {
        allowed: false,
        reason: changesResult.reason,
        message: null, // Silent - no message to user
        warnings,
        details: { changedFields: changesResult.changedFields }
      };
    }
  }
  
  // 7. Check stage advancement validity - block backward movement
  if (action === 'OPPORTUNITY_STAGE_ADVANCED' && options.oldStage && options.newStage) {
    const stageResult = validateStageAdvancement(options.oldStage, options.newStage);
    if (!stageResult.valid) {
      warnings.push('Stage not advancing forward');
      await logBlockedActivity(userId, action, stageResult.reason, {
        oldStage: options.oldStage,
        newStage: options.newStage,
        entityId
      });
      // Block backward stage movement
      return {
        allowed: false,
        reason: stageResult.reason,
        message: null, // Silent
        warnings,
        details: { oldStage: options.oldStage, newStage: options.newStage }
      };
    }
  }
  
  // All checks passed (or only logged warnings)
  return {
    allowed: true,
    reason: null,
    message: null,
    warnings,
    details: {}
  };
}

/**
 * Record successful point action (updates cooldown, daily count, and entity tracking)
 * @param {string} userId - User ID
 * @param {string} action - Action type
 * @param {string} entityId - Optional entity ID
 * @param {string} stageOrVariant - Optional stage for stage-based tracking
 */
export async function recordPointAction(userId, action, entityId = null, stageOrVariant = null) {
  await Promise.all([
    recordActionTime(userId, action, entityId),
    incrementDailyCount(userId, action),
    recordEntityActionAwarded(userId, action, entityId, stageOrVariant)
  ]);
}

// ============================================
// ABUSE DETECTION & REPORTING
// ============================================

/**
 * Flag suspicious activity for admin review
 */
export async function flagSuspiciousActivity(userId, activityType, details) {
  try {
    const flagRef = doc(collection(db, collectionPath('gamificationFlags')));
    await setDoc(flagRef, {
      userId,
      activityType,
      details,
      status: 'pending_review',
      createdAt: serverTimestamp()
    });
    console.warn('[AntiAbuse] Flagged suspicious activity:', activityType, 'for user:', userId);
  } catch (error) {
    console.error('[AntiAbuse] Failed to flag activity:', error);
  }
}

/**
 * Get abuse metrics for a user (for admin reporting)
 * @returns {Promise<object>} Abuse metrics
 */
export async function getUserAbuseMetrics(userId, dateRange = 30) {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange);
    
    // Get points log for the user
    const logsQuery = query(
      collection(db, collectionPath('pointsLog')),
      where('userId', '==', userId),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      orderBy('timestamp', 'desc'),
      limit(1000)
    );
    
    const logsSnap = await getDocs(logsQuery);
    const logs = logsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Analyze patterns
    const actionCounts = {};
    const hourlyDistribution = new Array(24).fill(0);
    const dailyCounts = {};
    let rapidFireCount = 0;
    
    let prevLog = null;
    for (const log of logs) {
      // Count by action
      actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
      
      // Hourly distribution
      const logTime = log.timestamp?.toDate?.() || new Date(log.timestamp);
      hourlyDistribution[logTime.getHours()]++;
      
      // Daily counts
      const dateKey = logTime.toISOString().split('T')[0];
      if (!dailyCounts[dateKey]) dailyCounts[dateKey] = {};
      dailyCounts[dateKey][log.action] = (dailyCounts[dateKey][log.action] || 0) + 1;
      
      // Detect rapid-fire (< 5 seconds between same action)
      if (prevLog && prevLog.action === log.action) {
        const prevTime = prevLog.timestamp?.toDate?.() || new Date(prevLog.timestamp);
        const timeDiff = (logTime - prevTime) / 1000;
        if (Math.abs(timeDiff) < 5) {
          rapidFireCount++;
        }
      }
      prevLog = log;
    }
    
    // Calculate suspicion score (0-100)
    let suspicionScore = 0;
    
    // High rapid-fire count increases suspicion
    if (rapidFireCount > 10) suspicionScore += 20;
    if (rapidFireCount > 50) suspicionScore += 30;
    
    // Unusual hourly patterns (e.g., all activity in one hour)
    const maxHourlyActivity = Math.max(...hourlyDistribution);
    const totalActivity = hourlyDistribution.reduce((a, b) => a + b, 0);
    if (totalActivity > 0 && maxHourlyActivity / totalActivity > 0.5) {
      suspicionScore += 15;
    }
    
    // Excessive daily counts
    for (const [date, actions] of Object.entries(dailyCounts)) {
      for (const [action, count] of Object.entries(actions)) {
        const limit = DAILY_ACTION_LIMITS[action] || 100;
        if (count > limit * 0.8) suspicionScore += 10;
        if (count >= limit) suspicionScore += 20;
      }
    }
    
    return {
      userId,
      dateRange,
      totalActions: logs.length,
      actionCounts,
      hourlyDistribution,
      dailyCounts,
      rapidFireCount,
      suspicionScore: Math.min(100, suspicionScore),
      suspicionLevel: suspicionScore < 25 ? 'low' : suspicionScore < 50 ? 'medium' : suspicionScore < 75 ? 'high' : 'critical',
      generatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('[AntiAbuse] Failed to get abuse metrics:', error);
    return null;
  }
}

/**
 * Generate abuse report for all users (admin only)
 */
export async function generateAbuseReport(options = {}) {
  const { dateRange = 30, minSuspicionScore = 25 } = options;
  
  try {
    // Get all users with gamification profiles
    const profilesQuery = query(
      collection(db, collectionPath('userGamification')),
      orderBy('totalPoints', 'desc'),
      limit(100)
    );
    
    const profilesSnap = await getDocs(profilesQuery);
    const users = profilesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    const report = {
      generatedAt: new Date().toISOString(),
      dateRange,
      minSuspicionScore,
      flaggedUsers: [],
      summary: {
        totalUsersAnalyzed: users.length,
        flaggedCount: 0,
        bySuspicionLevel: { low: 0, medium: 0, high: 0, critical: 0 }
      }
    };
    
    for (const user of users) {
      const metrics = await getUserAbuseMetrics(user.id, dateRange);
      if (metrics && metrics.suspicionScore >= minSuspicionScore) {
        report.flaggedUsers.push({
          userId: user.id,
          displayName: user.displayName || user.userId,
          totalPoints: user.totalPoints,
          ...metrics
        });
        report.summary.flaggedCount++;
      }
      if (metrics) {
        report.summary.bySuspicionLevel[metrics.suspicionLevel]++;
      }
    }
    
    // Sort by suspicion score descending
    report.flaggedUsers.sort((a, b) => b.suspicionScore - a.suspicionScore);
    
    return report;
  } catch (error) {
    console.error('[AntiAbuse] Failed to generate abuse report:', error);
    return null;
  }
}

// ============================================
// CONSTANTS FOR EXTERNAL USE
// ============================================

export const ABUSE_PREVENTION_CONFIG = {
  cooldowns: ACTION_COOLDOWNS,
  dailyLimits: DAILY_ACTION_LIMITS,
  minimumContent: MINIMUM_CONTENT_REQUIREMENTS,
  stageOrder: OPPORTUNITY_STAGE_ORDER,
  nonScoringStages: NON_SCORING_STAGES
};

// ============================================
// ADMIN REVIEW FUNCTIONS
// ============================================

/**
 * Get blocked activity log for admin review
 * @param {object} options - Query options
 * @returns {Promise<Array>} List of blocked activities
 */
export async function getBlockedActivityLog(options = {}) {
  const { 
    userId = null, 
    reviewed = null,
    awarded = null,
    limitCount = 100,
    dateRange = null,  // null = current year
    year = null        // specific year to filter
  } = options;
  
  try {
    // Calculate date range - default to current year
    let startDate;
    if (year) {
      startDate = new Date(`${year}-01-01T00:00:00`);
    } else if (dateRange) {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);
    } else {
      // Default: current year
      startDate = new Date(`${new Date().getFullYear()}-01-01T00:00:00`);
    }
    
    let q = query(
      collection(db, collectionPath('gamificationBlockedLog')),
      where('createdAt', '>=', Timestamp.fromDate(startDate)),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    let logs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    
    // Client-side filtering for additional criteria
    if (userId) {
      logs = logs.filter(l => l.userId === userId);
    }
    if (reviewed !== null && reviewed !== undefined) {
      logs = logs.filter(l => !!l.reviewed === reviewed);
    }
    // Handle awarded filter - treat undefined/null as not awarded
    if (awarded === true) {
      logs = logs.filter(l => l.awarded === true);
    } else if (awarded === false) {
      logs = logs.filter(l => !l.awarded); // undefined, null, or false
    }
    
    return logs;
  } catch (error) {
    console.error('[AntiAbuse] Failed to get blocked activity log:', error);
    return [];
  }
}

/**
 * Mark blocked activity as reviewed
 * @param {string} adminId - Admin user ID who reviewed
 * @param {string} logId - The blocked activity log ID
 * @param {string} adminNotes - Notes from the admin
 */
export async function markBlockedActivityReviewed(adminId, logId, adminNotes = '') {
  try {
    const logRef = doc(db, collectionPath('gamificationBlockedLog'), logId);
    await updateDoc(logRef, {
      reviewed: true,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminId,
      adminNotes
    });
    return true;
  } catch (error) {
    console.error('[AntiAbuse] Failed to mark activity reviewed:', error);
    return false;
  }
}

/**
 * Mark that points were manually awarded for a blocked activity
 * @param {string} adminId - Admin user ID who awarded
 * @param {string} logId - The blocked activity log ID
 * @param {number} pointsAwarded - Points that were awarded
 * @param {string} adminNotes - Notes from the admin
 */
export async function markBlockedActivityAwarded(adminId, logId, pointsAwarded, adminNotes = '') {
  try {
    const logRef = doc(db, collectionPath('gamificationBlockedLog'), logId);
    await updateDoc(logRef, {
      reviewed: true,
      awarded: true,
      awardedPoints: pointsAwarded,
      reviewedAt: serverTimestamp(),
      reviewedBy: adminId,
      adminNotes
    });
    return true;
  } catch (error) {
    console.error('[AntiAbuse] Failed to mark activity awarded:', error);
    return false;
  }
}

/**
 * Bulk dismiss multiple blocked activities
 * @param {string} adminId - Admin user ID
 * @param {string[]} logIds - Array of blocked activity log IDs to dismiss
 * @param {string} adminNotes - Notes from the admin
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function bulkDismissBlockedActivities(adminId, logIds, adminNotes = 'Bulk dismissed by admin') {
  let success = 0;
  let failed = 0;
  
  for (const logId of logIds) {
    try {
      const result = await markBlockedActivityReviewed(adminId, logId, adminNotes);
      if (result) success++;
      else failed++;
    } catch (error) {
      console.error(`[AntiAbuse] Failed to dismiss ${logId}:`, error);
      failed++;
    }
  }
  
  return { success, failed };
}

/**
 * Bulk award points for multiple blocked activities
 * @param {string} adminId - Admin user ID
 * @param {Array<{id: string, pointsWouldHaveAwarded: number, userId: string}>} logs - Array of blocked activity logs to award
 * @param {string} adminNotes - Notes from the admin
 * @returns {Promise<{success: number, failed: number, totalPointsAwarded: number}>}
 */
export async function bulkAwardBlockedActivities(adminId, logs, adminNotes = 'Bulk awarded by admin') {
  let success = 0;
  let failed = 0;
  let totalPointsAwarded = 0;
  
  for (const log of logs) {
    try {
      const points = log.pointsWouldHaveAwarded || 0;
      const result = await markBlockedActivityAwarded(adminId, log.id, points, adminNotes);
      if (result) {
        success++;
        totalPointsAwarded += points;
        
        // Also award the points to the user (if points > 0)
        if (points > 0 && log.userId) {
          const { adminAdjustPoints } = await import('./gamificationUtils');
          await adminAdjustPoints(adminId, log.userId, points, `Blocked activity manually approved: ${adminNotes}`);
        }
      } else {
        failed++;
      }
    } catch (error) {
      console.error(`[AntiAbuse] Failed to award ${log.id}:`, error);
      failed++;
    }
  }
  
  return { success, failed, totalPointsAwarded };
}

/**
 * Get summary stats of blocked activities for dashboard
 * @param {number} year - Year to get summary for (defaults to current year)
 */
export async function getBlockedActivitySummary(year = null) {
  try {
    const targetYear = year || new Date().getFullYear();
    const logs = await getBlockedActivityLog({ year: targetYear, limitCount: 1000 });
    
    const summary = {
      total: logs.length,
      pending: logs.filter(l => !l.reviewed && !l.awarded).length,
      reviewed: logs.filter(l => l.reviewed && !l.awarded).length,
      awarded: logs.filter(l => l.awarded === true).length,
      totalBlocked: logs.length,
      year: targetYear,
      byReason: {},
      byAction: {},
      byUser: {}
    };
    
    for (const log of logs) {
      // Count by reason
      const reason = log.blockReason || log.reason || 'unknown';
      summary.byReason[reason] = (summary.byReason[reason] || 0) + 1;
      
      // Count by action
      summary.byAction[log.action] = (summary.byAction[log.action] || 0) + 1;
      
      // Count by user
      summary.byUser[log.userId] = (summary.byUser[log.userId] || 0) + 1;
    }
    
    return summary;
  } catch (error) {
    console.error('[AntiAbuse] Failed to get blocked activity summary:', error);
    return null;
  }
}
